// ============================================================================
// Inventory Management Mock Data Service
// ============================================================================
// Description: Mock data service for Project Inventory Management system
// Used by: SDLC Settings tab for development and testing
// Note: This will be replaced with real API calls during integration
// ============================================================================

import {
  InventoryCategory,
  InventoryType,
  InventoryStatus,
  ProjectInventory,
  InventoryCluster,
  InventoryClusterRelationship,
  InventoryListResponse,
  InventoryResponse,
  InventoryStatistics
} from '../types/inventory';

// ============================================================================
// MOCK DATA: Categories
// ============================================================================
const mockCategories: InventoryCategory[] = [
  {
    id: 1,
    category_code: 'DATABASE',
    category_name: 'Database Objects',
    category_description: 'Tables, views, stored procedures, and database-related components',
    display_order: 1,
    is_active: true,
    created_at: '2025-10-01T10:00:00Z',
    updated_at: '2025-10-01T10:00:00Z',
    created_by: 1
  },
  {
    id: 2,
    category_code: 'BACKEND',
    category_name: 'Backend Services',
    category_description: 'API routes, models, services, and server-side components',
    display_order: 2,
    is_active: true,
    created_at: '2025-10-01T10:00:00Z',
    updated_at: '2025-10-01T10:00:00Z',
    created_by: 1
  },
  {
    id: 3,
    category_code: 'FRONTEND',
    category_name: 'Frontend Components',
    category_description: 'React components, pages, services, and client-side code',
    display_order: 3,
    is_active: true,
    created_at: '2025-10-01T10:00:00Z',
    updated_at: '2025-10-01T10:00:00Z',
    created_by: 1
  },
  {
    id: 4,
    category_code: 'TESTS',
    category_name: 'Test Assets',
    category_description: 'Unit tests, integration tests, E2E tests, and testing utilities',
    display_order: 4,
    is_active: true,
    created_at: '2025-10-01T10:00:00Z',
    updated_at: '2025-10-01T10:00:00Z',
    created_by: 1
  },
  {
    id: 5,
    category_code: 'DOCS',
    category_name: 'Documentation',
    category_description: 'Technical documentation, API specs, user guides, and README files',
    display_order: 5,
    is_active: true,
    created_at: '2025-10-01T10:00:00Z',
    updated_at: '2025-10-01T10:00:00Z',
    created_by: 1
  },
  {
    id: 6,
    category_code: 'CONFIG',
    category_name: 'Configuration',
    category_description: 'Docker files, package.json, environment configs, and setup scripts',
    display_order: 6,
    is_active: true,
    created_at: '2025-10-01T10:00:00Z',
    updated_at: '2025-10-01T10:00:00Z',
    created_by: 1
  }
];

// ============================================================================
// MOCK DATA: Types
// ============================================================================
const mockTypes: InventoryType[] = [
  // Database types
  {
    id: 1,
    category_id: 1,
    type_code: 'TABLE',
    type_name: 'Database Table',
    type_description: 'PostgreSQL table definitions',
    file_extensions: '.sql',
    default_base_path: 'database/schemas/',
    supports_versioning: true,
    supports_dependencies: true,
    display_order: 1,
    is_active: true,
    created_at: '2025-10-01T10:00:00Z',
    updated_at: '2025-10-01T10:00:00Z',
    created_by: 1,
    category_name: 'Database Objects'
  },
  {
    id: 2,
    category_id: 1,
    type_code: 'VIEW',
    type_name: 'Database View',
    type_description: 'SQL views and materialized views',
    file_extensions: '.sql',
    default_base_path: 'database/schemas/',
    supports_versioning: true,
    supports_dependencies: true,
    display_order: 2,
    is_active: true,
    created_at: '2025-10-01T10:00:00Z',
    updated_at: '2025-10-01T10:00:00Z',
    created_by: 1,
    category_name: 'Database Objects'
  },
  // Backend types
  {
    id: 3,
    category_id: 2,
    type_code: 'API_ROUTE',
    type_name: 'API Route',
    type_description: 'Flask route handlers and blueprints',
    file_extensions: '.py',
    default_base_path: 'backend/routes/',
    supports_versioning: true,
    supports_dependencies: true,
    display_order: 1,
    is_active: true,
    created_at: '2025-10-01T10:00:00Z',
    updated_at: '2025-10-01T10:00:00Z',
    created_by: 1,
    category_name: 'Backend Services'
  },
  {
    id: 4,
    category_id: 2,
    type_code: 'MODEL',
    type_name: 'Data Model',
    type_description: 'Database models and business logic',
    file_extensions: '.py',
    default_base_path: 'backend/models/',
    supports_versioning: true,
    supports_dependencies: true,
    display_order: 2,
    is_active: true,
    created_at: '2025-10-01T10:00:00Z',
    updated_at: '2025-10-01T10:00:00Z',
    created_by: 1,
    category_name: 'Backend Services'
  },
  // Frontend types
  {
    id: 5,
    category_id: 3,
    type_code: 'REACT_COMPONENT',
    type_name: 'React Component',
    type_description: 'Reusable React components',
    file_extensions: '.tsx,.jsx',
    default_base_path: 'frontend/src/components/',
    supports_versioning: true,
    supports_dependencies: true,
    display_order: 1,
    is_active: true,
    created_at: '2025-10-01T10:00:00Z',
    updated_at: '2025-10-01T10:00:00Z',
    created_by: 1,
    category_name: 'Frontend Components'
  },
  {
    id: 6,
    category_id: 3,
    type_code: 'SERVICE_MODULE',
    type_name: 'Frontend Service',
    type_description: 'API services and data layer',
    file_extensions: '.ts,.js',
    default_base_path: 'frontend/src/services/',
    supports_versioning: true,
    supports_dependencies: true,
    display_order: 2,
    is_active: true,
    created_at: '2025-10-01T10:00:00Z',
    updated_at: '2025-10-01T10:00:00Z',
    created_by: 1,
    category_name: 'Frontend Components'
  }
];

// ============================================================================
// MOCK DATA: Statuses
// ============================================================================
const mockStatuses: InventoryStatus[] = [
  {
    id: 1,
    status_code: 'ACTIVE',
    status_name: 'Active',
    status_description: 'Currently in use and maintained',
    status_color: '#28a745',
    is_active_state: true,
    display_order: 1,
    is_active: true,
    created_at: '2025-10-01T10:00:00Z',
    updated_at: '2025-10-01T10:00:00Z',
    created_by: 1
  },
  {
    id: 2,
    status_code: 'DEPRECATED',
    status_name: 'Deprecated',
    status_description: 'Marked for retirement, use discouraged',
    status_color: '#ffc107',
    is_active_state: false,
    display_order: 2,
    is_active: true,
    created_at: '2025-10-01T10:00:00Z',
    updated_at: '2025-10-01T10:00:00Z',
    created_by: 1
  },
  {
    id: 3,
    status_code: 'PLANNED',
    status_name: 'Planned',
    status_description: 'Planned for development but not yet implemented',
    status_color: '#17a2b8',
    is_active_state: true,
    display_order: 3,
    is_active: true,
    created_at: '2025-10-01T10:00:00Z',
    updated_at: '2025-10-01T10:00:00Z',
    created_by: 1
  },
  {
    id: 4,
    status_code: 'CRITICAL',
    status_name: 'Critical',
    status_description: 'Essential system component, handle with care',
    status_color: '#dc3545',
    is_active_state: true,
    display_order: 4,
    is_active: true,
    created_at: '2025-10-01T10:00:00Z',
    updated_at: '2025-10-01T10:00:00Z',
    created_by: 1
  }
];

// ============================================================================
// MOCK DATA: Clusters
// ============================================================================
const mockClusters: InventoryCluster[] = [
  {
    id: 1,
    cluster_code: 'AUTH_SYSTEM',
    cluster_name: 'Authentication System',
    cluster_description: 'Complete user authentication and session management system',
    cluster_type: 'SYSTEM',
    business_domain: 'Security',
    cluster_level: 1,
    cluster_status: 'ACTIVE',
    priority_level: 'CRITICAL',
    cluster_owner: 1,
    responsible_team: 'Backend Team',
    complexity_estimate: 'HIGH',
    completion_percentage: 95.0,
    documentation_notes: 'Core authentication system handling login, sessions, and user management',
    is_active: true,
    created_at: '2025-10-01T10:00:00Z',
    updated_at: '2025-10-01T10:00:00Z',
    created_by: 1,
    owner_name: 'John Doe',
    member_count: 5
  },
  {
    id: 2,
    cluster_code: 'AUTHORITY_MODULE',
    cluster_name: 'Authority Management Module',
    cluster_description: 'Hierarchical authority management with maker-checker workflow',
    cluster_type: 'MODULE',
    business_domain: 'Governance',
    cluster_level: 1,
    cluster_status: 'ACTIVE',
    priority_level: 'HIGH',
    cluster_owner: 1,
    responsible_team: 'Backend Team',
    complexity_estimate: 'HIGH',
    completion_percentage: 90.0,
    documentation_notes: 'Comprehensive authority lifecycle management with approval workflows',
    is_active: true,
    created_at: '2025-10-01T10:00:00Z',
    updated_at: '2025-10-01T10:00:00Z',
    created_by: 1,
    owner_name: 'John Doe',
    member_count: 5
  },
  {
    id: 3,
    cluster_code: 'IT_APP_LIFECYCLE',
    cluster_name: 'IT Application Lifecycle',
    cluster_description: 'Complete IT application management from registration to retirement',
    cluster_type: 'FEATURE',
    business_domain: 'Asset Management',
    cluster_level: 1,
    cluster_status: 'ACTIVE',
    priority_level: 'MEDIUM',
    cluster_owner: 2,
    responsible_team: 'Business Team',
    complexity_estimate: 'MEDIUM',
    completion_percentage: 75.0,
    documentation_notes: 'Tracks IT applications, versions, renewals, and lifecycle events',
    is_active: true,
    created_at: '2025-10-01T10:00:00Z',
    updated_at: '2025-10-01T10:00:00Z',
    created_by: 1,
    owner_name: 'Jane Smith',
    member_count: 3
  },
  {
    id: 4,
    cluster_code: 'DATA_TABLES_UI',
    cluster_name: 'Data Tables UI Framework',
    cluster_description: 'Reusable data table components and patterns',
    cluster_type: 'COMPONENT',
    business_domain: 'User Interface',
    cluster_level: 1,
    cluster_status: 'ACTIVE',
    priority_level: 'MEDIUM',
    cluster_owner: 3,
    responsible_team: 'Frontend Team',
    complexity_estimate: 'MEDIUM',
    completion_percentage: 85.0,
    documentation_notes: 'Standardized data table implementation with pagination and filtering',
    is_active: true,
    created_at: '2025-10-01T10:00:00Z',
    updated_at: '2025-10-01T10:00:00Z',
    created_by: 1,
    owner_name: 'Mike Johnson',
    member_count: 3
  },
  {
    id: 5,
    cluster_code: 'DEV_ENVIRONMENT',
    cluster_name: 'Development Environment',
    cluster_description: 'Complete development environment setup and management',
    cluster_type: 'SYSTEM',
    business_domain: 'Infrastructure',
    cluster_level: 1,
    cluster_status: 'ACTIVE',
    priority_level: 'HIGH',
    cluster_owner: 4,
    responsible_team: 'DevOps Team',
    complexity_estimate: 'HIGH',
    completion_percentage: 92.0,
    documentation_notes: 'Docker-based development environment with automated setup and management',
    is_active: true,
    created_at: '2025-10-01T10:00:00Z',
    updated_at: '2025-10-01T10:00:00Z',
    created_by: 1,
    owner_name: 'Sarah Wilson',
    member_count: 5
  },
  {
    id: 6,
    cluster_code: 'TESTING_FRAMEWORK',
    cluster_name: 'Testing Framework',
    cluster_description: 'Comprehensive testing infrastructure and test suites',
    cluster_type: 'SYSTEM',
    business_domain: 'Quality Assurance',
    cluster_level: 1,
    cluster_status: 'IN_PROGRESS',
    priority_level: 'HIGH',
    cluster_owner: 5,
    responsible_team: 'QA Team',
    complexity_estimate: 'MEDIUM',
    completion_percentage: 60.0,
    documentation_notes: 'Multi-tier testing approach with unit, integration, and E2E tests',
    is_active: true,
    created_at: '2025-10-01T10:00:00Z',
    updated_at: '2025-10-01T10:00:00Z',
    created_by: 1,
    owner_name: 'Alex Brown',
    member_count: 3
  }
];

// ============================================================================
// MOCK DATA: Inventory Items
// ============================================================================
const mockInventory: ProjectInventory[] = [
  {
    id: 1,
    inventory_code: 'DB_USERS_001',
    name: 'Users Table',
    description: 'Main user authentication and profile table',
    category_id: 1,
    type_id: 1,
    status_id: 4,
    folder_path: '/database/schemas/',
    file_name: '01-schema.sql',
    file_extension: '.sql',
    full_file_path: '/database/schemas/01-schema.sql',
    programming_language: 'SQL',
    framework_technology: 'PostgreSQL',
    estimated_complexity: 'MEDIUM',
    is_active: true,
    is_deprecated: false,
    is_critical: true,
    is_documented: true,
    owned_by_user: 1,
    team_responsible: 'Backend Team',
    business_purpose: 'Store user authentication data and profiles',
    usage_frequency: 'DAILY',
    user_impact_level: 'HIGH',
    has_unit_tests: false,
    has_integration_tests: true,
    has_e2e_tests: false,
    test_coverage_percentage: 85.5,
    dependency_count: 0,
    dependent_count: 3,
    search_keywords: 'users authentication profile login',
    created_at: '2025-09-01T10:00:00Z',
    updated_at: '2025-10-01T10:00:00Z',
    created_by: 1,
    category_name: 'Database Objects',
    category_code: 'DATABASE',
    type_name: 'Database Table',
    type_code: 'TABLE',
    status_name: 'Critical',
    status_code: 'CRITICAL',
    status_color: '#dc3545',
    owner_name: 'John Doe'
  },
  {
    id: 2,
    inventory_code: 'DB_AUTH_002',
    name: 'Authorities Table',
    description: 'Hierarchical authority management structure',
    category_id: 1,
    type_id: 1,
    status_id: 1,
    folder_path: '/database/schemas/',
    file_name: '01-schema.sql',
    file_extension: '.sql',
    full_file_path: '/database/schemas/01-schema.sql',
    programming_language: 'SQL',
    framework_technology: 'PostgreSQL',
    estimated_complexity: 'HIGH',
    is_active: true,
    is_deprecated: false,
    is_critical: true,
    is_documented: true,
    owned_by_user: 1,
    team_responsible: 'Backend Team',
    business_purpose: 'Manage organizational authorities and hierarchy',
    usage_frequency: 'DAILY',
    user_impact_level: 'HIGH',
    has_unit_tests: false,
    has_integration_tests: true,
    has_e2e_tests: false,
    test_coverage_percentage: 78.3,
    dependency_count: 1,
    dependent_count: 2,
    search_keywords: 'authorities hierarchy organization approval',
    created_at: '2025-09-01T10:00:00Z',
    updated_at: '2025-10-01T10:00:00Z',
    created_by: 1,
    category_name: 'Database Objects',
    category_code: 'DATABASE',
    type_name: 'Database Table',
    type_code: 'TABLE',
    status_name: 'Active',
    status_code: 'ACTIVE',
    status_color: '#28a745',
    owner_name: 'John Doe'
  },
  {
    id: 3,
    inventory_code: 'BE_AUTH_001',
    name: 'Authority Routes',
    description: 'CRUD operations for authority management',
    category_id: 2,
    type_id: 3,
    status_id: 1,
    folder_path: '/backend/routes/',
    file_name: 'authority.py',
    file_extension: '.py',
    full_file_path: '/backend/routes/authority.py',
    programming_language: 'Python',
    framework_technology: 'Flask',
    estimated_complexity: 'HIGH',
    is_active: true,
    is_deprecated: false,
    is_critical: true,
    is_documented: true,
    owned_by_user: 1,
    team_responsible: 'Backend Team',
    business_purpose: 'API endpoints for authority CRUD operations',
    usage_frequency: 'DAILY',
    user_impact_level: 'HIGH',
    has_unit_tests: true,
    has_integration_tests: true,
    has_e2e_tests: false,
    test_coverage_percentage: 89.2,
    dependency_count: 2,
    dependent_count: 1,
    search_keywords: 'authority API routes CRUD flask',
    created_at: '2025-09-01T10:00:00Z',
    updated_at: '2025-10-01T10:00:00Z',
    created_by: 1,
    category_name: 'Backend Services',
    category_code: 'BACKEND',
    type_name: 'API Route',
    type_code: 'API_ROUTE',
    status_name: 'Active',
    status_code: 'ACTIVE',
    status_color: '#28a745',
    owner_name: 'John Doe'
  },
  {
    id: 4,
    inventory_code: 'FE_AUTH_001',
    name: 'Authority Tree Component',
    description: 'Hierarchical display of authorities with CRUD operations',
    category_id: 3,
    type_id: 5,
    status_id: 1,
    folder_path: '/frontend/src/components/',
    file_name: 'AuthorityTreeTable.tsx',
    file_extension: '.tsx',
    full_file_path: '/frontend/src/components/AuthorityTreeTable.tsx',
    programming_language: 'TypeScript',
    framework_technology: 'React',
    estimated_complexity: 'HIGH',
    is_active: true,
    is_deprecated: false,
    is_critical: false,
    is_documented: true,
    owned_by_user: 3,
    team_responsible: 'Frontend Team',
    business_purpose: 'Display and manage authority hierarchy',
    usage_frequency: 'DAILY',
    user_impact_level: 'MEDIUM',
    has_unit_tests: true,
    has_integration_tests: false,
    has_e2e_tests: false,
    test_coverage_percentage: 73.6,
    dependency_count: 2,
    dependent_count: 0,
    search_keywords: 'authority tree component React hierarchy',
    created_at: '2025-09-15T10:00:00Z',
    updated_at: '2025-10-01T10:00:00Z',
    created_by: 1,
    category_name: 'Frontend Components',
    category_code: 'FRONTEND',
    type_name: 'React Component',
    type_code: 'REACT_COMPONENT',
    status_name: 'Active',
    status_code: 'ACTIVE',
    status_color: '#28a745',
    owner_name: 'Mike Johnson'
  }
];

// ============================================================================
// MOCK DATA: Cluster Relationships
// ============================================================================
const mockClusterRelationships: InventoryClusterRelationship[] = [
  {
    id: 1,
    inventory_id: 1,
    cluster_id: 1,
    relationship_type: 'CORE',
    importance_level: 'CRITICAL',
    role_in_cluster: 'Primary user data storage',
    added_at: '2025-09-01T10:00:00Z',
    added_by: 1,
    is_active: true,
    created_at: '2025-09-01T10:00:00Z',
    updated_at: '2025-10-01T10:00:00Z',
    created_by: 1,
    inventory_name: 'Users Table',
    inventory_code: 'DB_USERS_001',
    cluster_name: 'Authentication System'
  },
  {
    id: 2,
    inventory_id: 2,
    cluster_id: 2,
    relationship_type: 'CORE',
    importance_level: 'CRITICAL',
    role_in_cluster: 'Authority data storage',
    added_at: '2025-09-01T10:00:00Z',
    added_by: 1,
    is_active: true,
    created_at: '2025-09-01T10:00:00Z',
    updated_at: '2025-10-01T10:00:00Z',
    created_by: 1,
    inventory_name: 'Authorities Table',
    inventory_code: 'DB_AUTH_002',
    cluster_name: 'Authority Management Module'
  },
  {
    id: 3,
    inventory_id: 3,
    cluster_id: 2,
    relationship_type: 'CORE',
    importance_level: 'CRITICAL',
    role_in_cluster: 'Authority API endpoints',
    added_at: '2025-09-01T10:00:00Z',
    added_by: 1,
    is_active: true,
    created_at: '2025-09-01T10:00:00Z',
    updated_at: '2025-10-01T10:00:00Z',
    created_by: 1,
    inventory_name: 'Authority Routes',
    inventory_code: 'BE_AUTH_001',
    cluster_name: 'Authority Management Module'
  },
  {
    id: 4,
    inventory_id: 4,
    cluster_id: 2,
    relationship_type: 'CORE',
    importance_level: 'HIGH',
    role_in_cluster: 'Authority UI component',
    added_at: '2025-09-15T10:00:00Z',
    added_by: 1,
    is_active: true,
    created_at: '2025-09-15T10:00:00Z',
    updated_at: '2025-10-01T10:00:00Z',
    created_by: 1,
    inventory_name: 'Authority Tree Component',
    inventory_code: 'FE_AUTH_001',
    cluster_name: 'Authority Management Module'
  }
];

// ============================================================================
// MOCK SERVICE CLASS
// ============================================================================
class MockInventoryService {
  // Simulate API delays
  private delay(ms: number = 500): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Categories
  async getCategories(): Promise<InventoryListResponse<InventoryCategory>> {
    await this.delay();
    return {
      success: true,
      data: mockCategories,
      count: mockCategories.length
    };
  }

  async createCategory(category: Omit<InventoryCategory, 'id' | 'created_at' | 'updated_at'>): Promise<InventoryResponse<InventoryCategory>> {
    await this.delay();
    const newCategory: InventoryCategory = {
      ...category,
      id: Math.max(...mockCategories.map(c => c.id)) + 1,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    mockCategories.push(newCategory);
    return { success: true, data: newCategory };
  }

  async updateCategory(id: number, updates: Partial<InventoryCategory>): Promise<InventoryResponse<InventoryCategory>> {
    await this.delay();
    const index = mockCategories.findIndex(c => c.id === id);
    if (index === -1) {
      return { success: false, data: {} as InventoryCategory, error: 'Category not found' };
    }
    mockCategories[index] = { ...mockCategories[index], ...updates, updated_at: new Date().toISOString() };
    return { success: true, data: mockCategories[index] };
  }

  async deleteCategory(id: number): Promise<InventoryResponse<boolean>> {
    await this.delay();
    const index = mockCategories.findIndex(c => c.id === id);
    if (index === -1) {
      return { success: false, data: false, error: 'Category not found' };
    }
    mockCategories.splice(index, 1);
    return { success: true, data: true };
  }

  // Types
  async getTypes(): Promise<InventoryListResponse<InventoryType>> {
    await this.delay();
    return {
      success: true,
      data: mockTypes,
      count: mockTypes.length
    };
  }

  async createType(type: Omit<InventoryType, 'id' | 'created_at' | 'updated_at'>): Promise<InventoryResponse<InventoryType>> {
    await this.delay();
    const newType: InventoryType = {
      ...type,
      id: Math.max(...mockTypes.map(t => t.id)) + 1,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    mockTypes.push(newType);
    return { success: true, data: newType };
  }

  async updateType(id: number, updates: Partial<InventoryType>): Promise<InventoryResponse<InventoryType>> {
    await this.delay();
    const index = mockTypes.findIndex(t => t.id === id);
    if (index === -1) {
      return { success: false, data: {} as InventoryType, error: 'Type not found' };
    }
    mockTypes[index] = { ...mockTypes[index], ...updates, updated_at: new Date().toISOString() };
    return { success: true, data: mockTypes[index] };
  }

  async deleteType(id: number): Promise<InventoryResponse<boolean>> {
    await this.delay();
    const index = mockTypes.findIndex(t => t.id === id);
    if (index === -1) {
      return { success: false, data: false, error: 'Type not found' };
    }
    mockTypes.splice(index, 1);
    return { success: true, data: true };
  }

  // Statuses
  async getStatuses(): Promise<InventoryListResponse<InventoryStatus>> {
    await this.delay();
    return {
      success: true,
      data: mockStatuses,
      count: mockStatuses.length
    };
  }

  async createStatus(status: Omit<InventoryStatus, 'id' | 'created_at' | 'updated_at'>): Promise<InventoryResponse<InventoryStatus>> {
    await this.delay();
    const newStatus: InventoryStatus = {
      ...status,
      id: Math.max(...mockStatuses.map(s => s.id)) + 1,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    mockStatuses.push(newStatus);
    return { success: true, data: newStatus };
  }

  async updateStatus(id: number, updates: Partial<InventoryStatus>): Promise<InventoryResponse<InventoryStatus>> {
    await this.delay();
    const index = mockStatuses.findIndex(s => s.id === id);
    if (index === -1) {
      return { success: false, data: {} as InventoryStatus, error: 'Status not found' };
    }
    mockStatuses[index] = { ...mockStatuses[index], ...updates, updated_at: new Date().toISOString() };
    return { success: true, data: mockStatuses[index] };
  }

  async deleteStatus(id: number): Promise<InventoryResponse<boolean>> {
    await this.delay();
    const index = mockStatuses.findIndex(s => s.id === id);
    if (index === -1) {
      return { success: false, data: false, error: 'Status not found' };
    }
    mockStatuses.splice(index, 1);
    return { success: true, data: true };
  }

  // Clusters
  async getClusters(): Promise<InventoryListResponse<InventoryCluster>> {
    await this.delay();
    return {
      success: true,
      data: mockClusters,
      count: mockClusters.length
    };
  }

  async getCluster(id: number): Promise<InventoryResponse<InventoryCluster>> {
    await this.delay();
    const cluster = mockClusters.find(c => c.id === id);
    if (!cluster) {
      return { success: false, data: {} as InventoryCluster, error: 'Cluster not found' };
    }
    return { success: true, data: cluster };
  }

  async createCluster(cluster: Omit<InventoryCluster, 'id' | 'created_at' | 'updated_at'>): Promise<InventoryResponse<InventoryCluster>> {
    await this.delay();
    const newCluster: InventoryCluster = {
      ...cluster,
      id: Math.max(...mockClusters.map(c => c.id)) + 1,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      member_count: 0
    };
    mockClusters.push(newCluster);
    return { success: true, data: newCluster };
  }

  async updateCluster(id: number, updates: Partial<InventoryCluster>): Promise<InventoryResponse<InventoryCluster>> {
    await this.delay();
    const index = mockClusters.findIndex(c => c.id === id);
    if (index === -1) {
      return { success: false, data: {} as InventoryCluster, error: 'Cluster not found' };
    }
    mockClusters[index] = { ...mockClusters[index], ...updates, updated_at: new Date().toISOString() };
    return { success: true, data: mockClusters[index] };
  }

  async deleteCluster(id: number): Promise<InventoryResponse<boolean>> {
    await this.delay();
    const index = mockClusters.findIndex(c => c.id === id);
    if (index === -1) {
      return { success: false, data: false, error: 'Cluster not found' };
    }
    mockClusters.splice(index, 1);
    return { success: true, data: true };
  }

  // Inventory Items
  async getInventoryItems(): Promise<InventoryListResponse<ProjectInventory>> {
    await this.delay();
    return {
      success: true,
      data: mockInventory,
      count: mockInventory.length
    };
  }

  async getInventoryItem(id: number): Promise<InventoryResponse<ProjectInventory>> {
    await this.delay();
    const item = mockInventory.find(i => i.id === id);
    if (!item) {
      return { success: false, data: {} as ProjectInventory, error: 'Inventory item not found' };
    }
    return { success: true, data: item };
  }

  async createInventoryItem(item: Omit<ProjectInventory, 'id' | 'created_at' | 'updated_at'>): Promise<InventoryResponse<ProjectInventory>> {
    await this.delay();
    const newItem: ProjectInventory = {
      ...item,
      id: Math.max(...mockInventory.map(i => i.id)) + 1,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    mockInventory.push(newItem);
    return { success: true, data: newItem };
  }

  // Cluster relationships
  async getClusterMembers(clusterId: number): Promise<InventoryListResponse<ProjectInventory>> {
    await this.delay();
    const relationships = mockClusterRelationships.filter(r => r.cluster_id === clusterId);
    const memberItems = mockInventory.filter(item => 
      relationships.some(rel => rel.inventory_id === item.id)
    );
    
    // Add relationship metadata to items
    const enrichedItems = memberItems.map(item => {
      const relationship = relationships.find(r => r.inventory_id === item.id);
      return {
        ...item,
        relationship_type: relationship?.relationship_type,
        importance_level: relationship?.importance_level,
        role_in_cluster: relationship?.role_in_cluster
      };
    });

    return {
      success: true,
      data: enrichedItems,
      count: enrichedItems.length
    };
  }

  // Statistics
  async getStatistics(): Promise<InventoryResponse<InventoryStatistics>> {
    await this.delay();
    const stats: InventoryStatistics = {
      total_components: mockInventory.length,
      active_components: mockInventory.filter(i => i.is_active).length,
      critical_components: mockInventory.filter(i => i.is_critical).length,
      deprecated_components: mockInventory.filter(i => i.is_deprecated).length,
      components_by_category: {
        'Database Objects': mockInventory.filter(i => i.category_code === 'DATABASE').length,
        'Backend Services': mockInventory.filter(i => i.category_code === 'BACKEND').length,
        'Frontend Components': mockInventory.filter(i => i.category_code === 'FRONTEND').length
      },
      components_by_status: {
        'Active': mockInventory.filter(i => i.status_code === 'ACTIVE').length,
        'Critical': mockInventory.filter(i => i.status_code === 'CRITICAL').length,
        'Deprecated': mockInventory.filter(i => i.status_code === 'DEPRECATED').length
      },
      avg_test_coverage: mockInventory.reduce((sum, item) => sum + (item.test_coverage_percentage || 0), 0) / mockInventory.length,
      total_clusters: mockClusters.length,
      avg_cluster_completion: mockClusters.reduce((sum, cluster) => sum + cluster.completion_percentage, 0) / mockClusters.length
    };

    return { success: true, data: stats };
  }
}

// Export singleton instance
export const mockInventoryService = new MockInventoryService();
export default mockInventoryService;